import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import StudentDetailsModal from '../../components/StudentDetailsModal';
import RegisterPoolApproveModal from '../../components/RegisterPoolApproveModal';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const SUBMISSION_STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  approved: { label: 'Approved', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  rejected: { label: 'Rejected', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' },
};

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.15)' : 'none',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#818cf8' },
  }),
};

function formatBatchLabel(batch) {
  const name = batch.batchInternalName || batch.batchName || batch.whatsappGroupName || `Batch #${batch.batchId}`;
  return batch.status ? `${name} (${batch.status})` : name;
}

// Renders the course name with a Weekday/Weekend badge -- option.label stays
// a plain string (used for react-select's built-in search matching); this
// only customizes what's actually painted for each option.
function formatCourseOptionLabel(option) {
  const schedule = option.scheduleType ? String(option.scheduleType).toLowerCase() : '';
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="truncate">{option.label}</span>
      {schedule && (
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            schedule === 'weekday' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {schedule === 'weekday' ? 'Weekday' : 'Weekend'}
        </span>
      )}
    </div>
  );
}

function initialsFromName(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function Avatar({ name, imagePath }) {
  if (imagePath) {
    return (
      <img
        src={getImageURL(imagePath)}
        alt={name}
        className="h-9 w-9 shrink-0 rounded-full object-cover"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
      {initialsFromName(name)}
    </div>
  );
}

export default function RegisterPool() {
  const [courseOptions, setCourseOptions] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coordinateCourseIds, setCoordinateCourseIds] = useState([]);

  const [batchOptions, setBatchOptions] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  const [search, setSearch] = useState('');

  const [selectedStudent, setSelectedStudent] = useState(null);

  const [generatingQr, setGeneratingQr] = useState(false);
  const [qrError, setQrError] = useState(null);

  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [approvingSubmission, setApprovingSubmission] = useState(null);
  const [busySubmissionId, setBusySubmissionId] = useState(null);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const loadSubmissions = () => {
    if (!selectedBatch) return;
    setSubmissionsLoading(true);
    axiosInstance.get(`/admin/register-pool/batches/${selectedBatch.value}/submissions`)
      .then((resp) => setSubmissions(resp.data?.submissions || []))
      .catch(() => setSubmissions([]))
      .finally(() => setSubmissionsLoading(false));
  };

  // Pending registrations for the selected batch, across every QR session
  // ever generated for it -- not just one currently open in another tab.
  useEffect(() => {
    setSubmissions([]);
    if (!selectedBatch) return;
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch]);

  const pendingSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'pending'),
    [submissions]
  );

  const rejectedSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'rejected'),
    [submissions]
  );

  const approvedSubmissions = useMemo(
    () => submissions.filter((s) => s.status === 'approved'),
    [submissions]
  );

  const handleRejectSubmission = async (submission) => {
    const result = await Swal.fire({
      title: 'Reject this registration?',
      html: `<strong>${submission.fullName}</strong>'s pending registration will be rejected.`,
      icon: 'warning',
      input: 'textarea',
      inputPlaceholder: 'Optional reason…',
      inputAttributes: { 'aria-label': 'Optional reason' },
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setBusySubmissionId(submission.id);
    try {
      await axiosInstance.post(`/admin/register-pool/submissions/${submission.id}/reject`, { reason: result.value || null });
      loadSubmissions();
    } catch (err) {
      Swal.fire({
        title: 'Failed to reject',
        text: err?.response?.data?.message || 'Failed to reject this submission.',
        icon: 'error',
        confirmButtonColor: '#4f46e5',
      });
    } finally {
      setBusySubmissionId(null);
    }
  };

  // Super-admin-only override: approve a submission that was already rejected.
  // Confirms first (since it overturns a prior decision), then hands off to
  // the same RegisterPoolApproveModal / create-student-and-registration flow
  // used for pending submissions.
  const handleApproveRejected = async (submission) => {
    const result = await Swal.fire({
      title: 'Approve this rejected registration?',
      html: `<strong>${submission.fullName}</strong>'s registration was previously <strong class="text-red-600">rejected</strong>${submission.reviewNote ? ` (reason: "${submission.reviewNote}")` : ''}. Approving now will override that rejection and create the student &amp; registration records.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, approve it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setShowRejectedModal(false);
    setApprovingSubmission(submission);
  };

  const handleGenerateQr = async () => {
    if (!selectedBatch || generatingQr) return;
    setGeneratingQr(true);
    setQrError(null);
    try {
      const resp = await axiosInstance.post('/admin/register-pool/sessions', { batchId: selectedBatch.value });
      const token = resp.data?.session?.token;
      if (!token) throw new Error('No token returned');
      window.open(`${window.location.origin}/admin/register-pool/qr/${token}`, '_blank', 'noopener');
    } catch (err) {
      setQrError(err?.response?.data?.message || 'Failed to generate the QR registration link.');
    } finally {
      setGeneratingQr(false);
    }
  };

  // Course options: the courses this staff member coordinates (super admins
  // get every course, same rule the rest of the admin app uses).
  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/admin/me/coordinate-courses')
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data || {};
        const superAdmin = Boolean(data.isSuperAdmin);
        setIsSuperAdmin(superAdmin);
        const coordinateCourses = data.coordinateCourses || {};
        const entries = Object.values(coordinateCourses);
        setCoordinateCourseIds(entries.map((c) => Number(c.courseId)));
        setCourseOptions(
          entries
            .filter((c) => superAdmin || c.isView)
            .map((c) => ({
              value: Number(c.courseId),
              label: c.shortName || c.fullName || `Course #${c.courseId}`,
              scheduleType: c.scheduleType || null,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCoursesLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Batch options reset and reload whenever the selected course changes.
  useEffect(() => {
    setSelectedBatch(null);
    setBatchOptions([]);
    setStudents([]);
    if (!selectedCourse) return undefined;

    let cancelled = false;
    setBatchesLoading(true);
    axiosInstance.get('/admin/batches', { params: { courseId: selectedCourse.value } })
      .then((resp) => {
        if (cancelled) return;
        const batches = resp.data?.batches || [];
        setBatchOptions(batches.map((b) => ({ value: b.batchId, label: formatBatchLabel(b) })));
      })
      .catch(() => { if (!cancelled) setBatchOptions([]); })
      .finally(() => { if (!cancelled) setBatchesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCourse]);

  // Student roster for the selected batch.
  const loadStudents = () => {
    if (!selectedBatch) return;
    setStudentsError(null);
    setStudentsLoading(true);
    axiosInstance.get('/admin/students', {
      params: { batchId: selectedBatch.value, registrationStatus: 'all', deliveryStatus: 'all', perPage: 200 },
    })
      .then((resp) => setStudents(resp.data?.students || []))
      .catch(() => setStudentsError('Failed to load students for this batch.'))
      .finally(() => setStudentsLoading(false));
  };

  useEffect(() => {
    setStudents([]);
    setStudentsError(null);
    if (!selectedBatch) return;
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      (s.fullName || '').toLowerCase().includes(q) ||
      (s.studentId || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.registrations || []).some((r) => (r.registrationNumber || '').toLowerCase().includes(q))
    );
  }, [students, search]);

  return (
    <DashboardLayout title="Register Pool">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Register Pool</h1>
          <p className="mt-1 text-sm text-gray-600">Pick a course and batch to browse its registered students.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Course</label>
            <Select
              value={selectedCourse}
              onChange={setSelectedCourse}
              options={courseOptions}
              formatOptionLabel={formatCourseOptionLabel}
              isLoading={coursesLoading}
              isClearable
              placeholder="Select a course…"
              styles={selectStyles}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-500">Batch</label>
            <Select
              value={selectedBatch}
              onChange={setSelectedBatch}
              options={batchOptions}
              isLoading={batchesLoading}
              isDisabled={!selectedCourse}
              isClearable
              placeholder={selectedCourse ? 'Select a batch…' : 'Select a course first'}
              styles={selectStyles}
            />
          </div>
        </div>

        {selectedBatch && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleGenerateQr}
              disabled={generatingQr}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.5h4.5v4.5h-4.5v-4.5zm0 10.5h4.5v4.5h-4.5v-4.5zm10.5-10.5h4.5v4.5h-4.5v-4.5zm1.5 9v1.5m-1.5 3h1.5m-1.5-1.5h3m0-3h1.5v1.5m0 3v1.5h-1.5" />
              </svg>
              {generatingQr ? 'Generating…' : 'Generate QR'}
            </button>
            <span className="text-xs text-gray-400">Opens a new tab with a QR code students can scan to self-register into this batch.</span>
            {qrError && <span className="text-xs font-medium text-red-600">{qrError}</span>}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowApprovedModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                Approved{approvedSubmissions.length > 0 ? ` (${approvedSubmissions.length})` : ''}
              </button>
              <button
                type="button"
                onClick={() => setShowRejectedModal(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Rejected{rejectedSubmissions.length > 0 ? ` (${rejectedSubmissions.length})` : ''}
              </button>
            </div>
          </div>
        )}

        {/* Pending registrations for this batch -- collected across every QR
            session ever generated for it, not just one currently open elsewhere. */}
        {selectedBatch && !submissionsLoading && pendingSubmissions.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-100 px-4 py-3">
              <span className="text-sm font-semibold text-amber-800">
                {pendingSubmissions.length} pending registration{pendingSubmissions.length !== 1 ? 's' : ''} to review
              </span>
              <button
                type="button"
                onClick={loadSubmissions}
                className="text-xs font-medium text-amber-700 underline decoration-dotted hover:text-amber-900"
              >
                Refresh
              </button>
            </div>
            <ul className="divide-y divide-amber-100">
              {pendingSubmissions.map((s) => {
                const meta = SUBMISSION_STATUS_META[s.status] || SUBMISSION_STATUS_META.pending;
                return (
                  <li key={s.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                    <Avatar name={s.fullName} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{s.fullName}</span>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                        {s.studentId && <span className="font-semibold text-indigo-600">{s.studentId}</span>}
                        {s.registrationNumber && <span>Reg #: {s.registrationNumber}</span>}
                        {s.telephone && <span>{s.telephone}</span>}
                        {s.email && <span className="truncate">{s.email}</span>}
                        <span>Submitted {fmtDateTime(s.submittedAt)}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        onClick={() => setApprovingSubmission(s)}
                        disabled={busySubmissionId === s.id}
                        className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectSubmission(s)}
                        disabled={busySubmissionId === s.id}
                        className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!selectedBatch ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-16 text-center text-sm text-gray-400">
            {selectedCourse ? 'Select a batch to see its students.' : 'Select a course, then a batch, to see its students.'}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
              <span className="text-sm font-semibold text-gray-700">
                {studentsLoading ? 'Loading students…' : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''}`}
              </span>
              <div className="flex items-center gap-2">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, ID, reg. no…"
                  className="w-64 rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={loadStudents}
                  disabled={studentsLoading}
                  title="Refresh student list"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg className={`h-3.5 w-3.5 ${studentsLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>

            {studentsLoading ? (
              <div className="px-4 py-12 text-center text-sm text-gray-400">Loading…</div>
            ) : studentsError ? (
              <div className="px-4 py-12 text-center text-sm text-red-500">{studentsError}</div>
            ) : filteredStudents.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-gray-400">
                {search ? 'No students match your search.' : 'No students registered to this batch.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Student</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Reg. No</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Telephone</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredStudents.map((s) => {
                      const reg = (s.registrations || []).find((r) => r.batchId === selectedBatch.value) || s.registrations?.[0];
                      return (
                        <tr
                          key={s.studentId}
                          onClick={() => setSelectedStudent(s)}
                          className="cursor-pointer transition hover:bg-indigo-50/60"
                        >
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-3">
                              <Avatar name={s.fullName} imagePath={s.imagePath} />
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-gray-900">{s.fullName || 'Unnamed'}</div>
                                <div className="truncate text-xs text-gray-400">{s.studentId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{reg?.registrationNumber || '—'}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{s.telephone || '—'}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-600">{s.email || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedStudent && (
        <StudentDetailsModal
          student={{ ...selectedStudent, coordinateCourseIds }}
          onClose={() => setSelectedStudent(null)}
        />
      )}

      {approvingSubmission && (
        <RegisterPoolApproveModal
          submission={approvingSubmission}
          batchId={selectedBatch?.value}
          onClose={() => setApprovingSubmission(null)}
          onApproved={() => {
            setApprovingSubmission(null);
            loadSubmissions();
          }}
        />
      )}

      {showApprovedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => setShowApprovedModal(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="text-base font-bold text-slate-800">
                Approved Registrations
                {approvedSubmissions.length > 0 ? ` (${approvedSubmissions.length})` : ''}
              </h3>
              <button
                onClick={() => setShowApprovedModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto">
              {approvedSubmissions.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-gray-400">No approved registrations for this batch.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {approvedSubmissions.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-3">
                      <Avatar name={s.fullName} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{s.fullName}</span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                            Approved
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          {s.createdStudentId && <span className="font-semibold text-indigo-600">{s.createdStudentId}</span>}
                          {s.registrationNumber && <span>Reg #: {s.registrationNumber}</span>}
                          {s.telephone && <span>{s.telephone}</span>}
                          {s.email && <span className="truncate">{s.email}</span>}
                          <span>Submitted {fmtDateTime(s.submittedAt)}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          <span>Registered {fmtDateTime(s.approvedAt)}{s.approvedByName ? ` by ${s.approvedByName}` : ''}</span>
                          {s.reviewNote && <span className="italic text-gray-400">Note: "{s.reviewNote}"</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showRejectedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          onClick={() => setShowRejectedModal(false)}
        >
          <div
            className="flex max-h-[80vh] w-full max-w-xl flex-col rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h3 className="text-base font-bold text-slate-800">
                Rejected Registrations
                {rejectedSubmissions.length > 0 ? ` (${rejectedSubmissions.length})` : ''}
              </h3>
              <button
                onClick={() => setShowRejectedModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto">
              {rejectedSubmissions.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-gray-400">No rejected registrations for this batch.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {rejectedSubmissions.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-3">
                      <Avatar name={s.fullName} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{s.fullName}</span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                            Rejected
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          {s.studentId && <span className="font-semibold text-indigo-600">{s.studentId}</span>}
                          {s.registrationNumber && <span>Reg #: {s.registrationNumber}</span>}
                          {s.telephone && <span>{s.telephone}</span>}
                          {s.email && <span className="truncate">{s.email}</span>}
                          <span>Submitted {fmtDateTime(s.submittedAt)}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          <span>Rejected {fmtDateTime(s.approvedAt)}{s.approvedByName ? ` by ${s.approvedByName}` : ''}</span>
                          {s.reviewNote && <span className="italic text-gray-400">"{s.reviewNote}"</span>}
                        </div>
                      </div>
                      {isSuperAdmin && (
                        <div className="flex shrink-0">
                          <button
                            onClick={() => handleApproveRejected(s)}
                            className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200"
                            title="Super-admin only: override this rejection and approve"
                          >
                            Approve Anyway
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
